import BaseModal from "../../../../components/ui/BaseModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseButton from "../../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../../components/ui/BaseAutoSelect";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../../store/toast/toastSlice";
import { fetchTeams } from "../../../../services/api/teams";
import { createDepartment, updateDepartment } from "../../../../services/api/departments";

const getValidationSchema = () =>
  yup.object({
    name: yup
      .string()
      .trim()
      .required("Enter Department name")
      .max(100, "Name must be at most 100 characters"),
    description: yup
      .string()
      .max(500, "Description must be at most 500 characters"),
    teamIds: yup.array().of(yup.string()),
  });

const AddEditDepartmentModal = ({
  open,
  close,
  selectedDepartment,
  setSelectedDepartment,
  isEditMode = true,
  setIsEditMode,
  refetchAllDepartments,
}) => {
  const dispatch = useDispatch();

  // All company teams (no pagination) to choose from.
  const { data: teamsData } = useQuery({
    queryKey: ["allTeamsForDepartments"],
    queryFn: () => fetchTeams({}),
  });

  const { mutateAsync: createDepartmentMutation, isLoading: isCreating } = useMutation({
    mutationFn: createDepartment,
  });

  const { mutateAsync: updateDepartmentMutation, isLoading: isUpdating } = useMutation({
    mutationFn: updateDepartment,
  });

  const handleSubmit = async (values) => {
    try {
      if (selectedDepartment) {
        await updateDepartmentMutation({
          id: selectedDepartment.id,
          name: values.name,
          description: values.description,
          teamIds: values.teamIds,
        });
        dispatch(showToast({ message: "Department updated successfully", type: "success" }));
      } else {
        await createDepartmentMutation({
          name: values.name,
          description: values.description,
          teamIds: values.teamIds,
        });
        dispatch(showToast({ message: "Department created successfully", type: "success" }));
      }
      refetchAllDepartments?.();
      setSelectedDepartment(null);
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  };

  // Opened from the name cell -> read-only, same as the bank account modal.
  const isReadOnly = !!selectedDepartment && !isEditMode;

  const formik = useFormik({
    initialValues: {
      name: selectedDepartment?.name || "",
      description: selectedDepartment?.description || "",
      teamIds: selectedDepartment?.teams?.map((t) => t.id) || [],
    },
    validationSchema: getValidationSchema(),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <BaseModal
        open={open}
        close={() => {
          close(false);
          setSelectedDepartment(null);
        }}
        className="-top-[200px] overflow-auto"
        style={{ width: "800px" }}
      >
        <div className="px-8 py-5">
          <h6 className="text-dark font-medium border-b pb-1">
            {selectedDepartment
              ? isEditMode
                ? "Update Department"
                : "Department Details"
              : "Create New Department"}
          </h6>
        </div>

        <div className="px-8 py-4 grid grid-cols-2 gap-x-6 gap-y-4">
          <div className="col-span-2">
            <BaseTextField
              disabled={isReadOnly}
              label={
                <span>
                  Department Name <span className="text-red-500">*</span>
                </span>
              }
              labelShrink={false}
              optional={false}
              id="name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </div>

          <div className="col-span-2">
            <BaseTextField
              disabled={isReadOnly}
              label="Description"
              labelShrink={false}
              optional={true}
              id="description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </div>

          <div className="col-span-2 mb-5">
            <BaseAutoSelect
              disabled={isReadOnly}
              labelShrink={false}
              label="Teams"
              multiple
              placeholder="Select teams"
              options={
                teamsData?.data?.data
                  ?.filter(
                    // Only teams not already linked to another department
                    // (teams on the department being edited stay selectable).
                    (team) =>
                      !team.departmentId ||
                      team.departmentId === selectedDepartment?.id
                  )
                  ?.map((team) => ({
                    label: team.name,
                    value: team.id,
                  })) || []
              }
              value={formik.values.teamIds}
              onChange={(e) => formik.setFieldValue("teamIds", e.target.value)}
              isClearable
              closeMenuOnSelect={false}
            />
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] text-primary hover:!text-white"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => {
              close(false);
              setSelectedDepartment(null);
              setIsEditMode?.(false);
            }}
          >
            Cancel
          </BaseButton>
          {!isReadOnly && (
          <BaseButton
            disabled={!formik.dirty || !formik.isValid || isCreating || isUpdating}
            onClick={formik.handleSubmit}
            loaderSize="22px"
            isLoading={isCreating || isUpdating}
            color="primary"
            className="py-[5px] px-[20px] min-w-32"
          >
            {selectedDepartment ? "Update" : "Create"}
          </BaseButton>
          )}
        </div>
      </BaseModal>
    </form>
  );
};

export default AddEditDepartmentModal;
